import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { redirect, useFetcher, useLoaderData } from 'react-router-dom';
import { changeLanguage } from '../../App.jsx';
import { otpLogin, requestOtp } from '../../api/auth/login.js';
import { getQuotationDetails } from '../../api/quotation/quotation.js';
import Footer from '../../components/layout/footer.jsx';
import { HeaderBar } from '../../components/layout/header-bar.jsx';
import Header from '../../components/layout/header.jsx';
import { RichTrans } from '../../components/ui';
import i18n from '../../i18n.js';
import { contactNumber } from '../../library/constants/cms.js';
import { generateRouteUrl } from '../../library/constants/routes.jsx';
import { LocalStorage } from '../../library/utilities/localstorage.js';
import styles from './login.module.scss';
import OtpInput from './otp-input.jsx';
import OtpResendButton from './otp-resend-button.jsx';

const RESENT_OTP_DELAY = 60 * 1000

const Login = () => {
  const { project_id, quotation, userLanguage, cms_language_code, cms_language_name, quotationToken, chatItems, isChatOrderPhase } = useLoaderData()
  const [otp, setOtp] = useState('');
  const fetcher = useFetcher()
  const { error = null } = fetcher.data || {}
  const { t } = useTranslation()

  const requestOtp = useCallback(() => {
    fetcher.submit({
      action: 'requestOtp'
    }, {
      method: 'post',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    requestOtp()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleOtpSubmit = (val) => {
    fetcher.submit({
      otp: val,
      action: 'authenticate'
    }, {
      method: 'post',
    })
  }

  return (
    <>
      <div className="sticky-header">
        <Header userLanguage={userLanguage} token={quotationToken} items={chatItems} isApproved={isChatOrderPhase} />
        <HeaderBar projectId={project_id} language={userLanguage} cmsLanguage={cms_language_code} name={cms_language_name}/>
      </div>
      <div className="container page">
        <div className="wrapper">
          <div className="main">
            <div className="box--white bg-lion">
              <div className={styles.loginWrapper}>
                <h2>{t("Enter Verification Number")}:</h2>
                <p className={styles.intro}>
                  {t("Input the received number in the space provided on the order page.")}
                  {quotation
                    ? t("Once verified, you'll be prompted to electronically sign the order confirmation.")
                    : t("Once verified, you'll be able to view/manage your order.")}
                </p>
                <img src="/icons/check_1_layerstyle.svg" alt="checked mail" style={{ margin: '1rem auto 2rem auto' }} />
                {!!error && (
                  <p className='error' style={{ margin: 0 }}>{error}</p>
                )}
                <OtpInput value={otp} valueLength={6} onChange={(val) => setOtp(val)} onSubmit={handleOtpSubmit} />
                <br />
                <OtpResendButton onClick={requestOtp} disabled={fetcher.state !== 'idle'} />
                <p className={styles.help}>
                  <RichTrans
                    defaults='If you haven’t received a code, please <a href="https://www.leoprinting.nl/contact" target="_blank" rel="noopener noreferrer">contact</a> us.'
                  />{' '}
                  <a className={styles.phone} href={`tel:${contactNumber[cms_language_code]}`}>{contactNumber[cms_language_code]}</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer userLanguage={userLanguage} />
    </>
  )
}
Login.Actions = {
  requestOtp: async ({ params, url }) => {
    const { quotationToken } = params
    //Check if otp is sent already
    const lastOtpTime = parseInt(LocalStorage(quotationToken).get('lastOtpRequest'))
    if (lastOtpTime && (Date.now() - lastOtpTime) < RESENT_OTP_DELAY) {
      return {
        error: i18n.t('Verification code already requested.')
      }
    }
    const from = new URL(url).searchParams.get('from')
    const purpose = from?.endsWith('/sign') ? 'sign' : 'access'
    try {
      await requestOtp(quotationToken, purpose)
    }
    catch (error) {
      LocalStorage(quotationToken).set('lastOtpRequest', null)
      return {
        error: error.message
      }
    }
    return true
  },
  authenticate: async ({ params, data, url }) => {
    const { quotationToken } = params
    const { otp } = data
    try {
      if (!otp || otp.length !== 6) {
        return {
          error: i18n.t("Invalid Verification Code")
        }
      }
      const { data } = await otpLogin({}, {
        otp,
        token: quotationToken
      })
      localStorage.setItem('USER', JSON.stringify(data));
      let params = new URL(url).searchParams;
      let from = params.get("from");
      const SAFE_FROM_RE = /^\/(dashboard|quotation|approvedquotation|additional-quotation)\//
      let redirectTo = (from && from.startsWith('/') && SAFE_FROM_RE.test(from))
        ? from
        : generateRouteUrl('Dashboard', { quotationToken })
      return redirect(redirectTo);
    }
    catch (error) {
      return {
        error: i18n.t(error.message)
      }
    }
  },
}

Login.Loader = async ({ params }) => {
  /**
    * todo: Add a mechanism to detect if an otp request is already
    * send to prevent sending multiple requests withien a specified 
    * period of time, may be using cookie expiration period
    */
  const { quotationToken } = params
  const { project_id, cms_language_code,cms_language_name, quotation, chatItems, isChatOrderPhase } = await getQuotationDetails(quotationToken)

  if (!project_id) {
    throw new Response("", { status: 404 });
  }

  let userLanguage = navigator.language.split('-')?.[0] ?? 'en'
  if (cms_language_code) {
    userLanguage = cms_language_code.split('_')?.[1] ?? cms_language_code
  }
  changeLanguage(userLanguage)
  return { project_id, quotation, userLanguage, cms_language_code,cms_language_name, quotationToken, chatItems, isChatOrderPhase }
}

export default Login
